import React, {
  useState,
  useContext,
  useCallback,
  useEffect,
  useMemo
} from "react";
import gql from "graphql-tag";
import { useQuery } from "@apollo/react-hooks";
import { gqlType } from "@hifieng/common";
import AppError from "../errors/AppError";
import { IOrganizations } from "../types/OrganizationTypes";
import useDeepCompareEffect from "use-deep-compare-effect";
import moment from "moment-timezone";

const INITIAL_COORDINATES_PAGE = 0;
// Number of coordinate elements (segments/assets/kmPosts) fetched per page
// per pipeline. Raise to reduce round-trips; lower to reduce first-payload size.
const COORDINATES_PAGE_SIZE = 500;
const NEXT_PAGE_INCREMENT = 1;
const FIRST_ORGANIZATION_INDEX = 0;
const MINUTES_IN_HOUR = 60;
const OFFSET_PADDING_LENGTH = 2;

type OrganizationWithPageInfoType = Omit<
  gqlType.Organization,
  "pipelineCoordinatesPageInfo"
> & {
  pipelineCoordinatesPageInfo: gqlType.PipelineCoordinatesPageInfo;
};

type GetOrganizationsDataType = {
  organizations?: Array<OrganizationWithPageInfoType>;
};

export const GET_ORGANIZATIONS_DATA = gql`
  query GetOrganizationsData(
    $pipelineCoordinatesPage: Int = 0
    $pipelineCoordinatesPageSize: Int = 500
  ) {
    organizations(
      pipelineCoordinatesPage: $pipelineCoordinatesPage
      pipelineCoordinatesPageSize: $pipelineCoordinatesPageSize
    ) {
      id
      name
      imageUrl
      utcOffset
      timezoneAbbr
      pipelines {
        id
        name
        color
        segments {
          id
          post
          coordinates {
            latitude
            longitude
          }
        }
        assets {
          id
          type
          post
          coordinates {
            latitude
            longitude
          }
        }
        kmPosts {
          post
          coordinates {
            latitude
            longitude
          }
        }
        interfaceOptions {
          hideFromMapLegend
          hideHeartbeat
          initialFocus
          zIndex
          hidePosts
        }
        chKpMaps {
          id
          name
          ch
          kp
          precision
          step
        }
      }
      pipelineCoordinatesPageInfo {
        page
        pageSize
        hasMore
      }
    }
  }
`;

export type OrganizationResponseDataType = {
  id: string;
  name: string;
  imageUrl: string;
  pipelines: Array<gqlType.Pipeline>;
  pointsOfInterest: Array<gqlType.PointOfInterest>;
};

const initialContext: IOrganizations = {
  activeOrg: undefined,
  setActiveOrg: () => {
    throw new Error("Organization context has not yet been initialized.");
  },
  organizations: undefined
};

type PropsType = {
  children: React.ReactNode;
};

const applyDaylightSavings = (
  org: OrganizationWithPageInfoType
): OrganizationWithPageInfoType => {
  const timezoneAbbreviation = org.timezoneAbbr;
  if (!timezoneAbbreviation) {
    return org;
  }

  // Uses IANA standard for timezone names. Long name needed for Daylight Savings to be applied.
  // utcOffset setting from config is ignored if IANA long name provided as setting.
  if (!moment.tz.zone(timezoneAbbreviation)) {
    return org;
  }

  // MST works here because there is only one MST. CST and PST don't work because there are multiple of those
  const momentObj = moment.tz(timezoneAbbreviation);
  const zoneAbbreviation = momentObj.zoneAbbr();

  // Orgs with MST (any IANA abbreviations) will be skipped using the condition below.
  // If org timezone already calculated this this will also be skipped since new timezone abbreviation is being used.
  // assign timezone abbreviation and calculate utc offset if the config abbr is different from actual ie. config is not abbreviated
  if (zoneAbbreviation === timezoneAbbreviation) {
    return org;
  }

  const utcOffset = momentObj.utcOffset();
  const sign = utcOffset >= 0 ? "+" : "-";
  const absoluteOffset = Math.abs(utcOffset);
  const hour = Math.floor(absoluteOffset / MINUTES_IN_HOUR);
  const minutes = absoluteOffset % MINUTES_IN_HOUR;
  const paddedHour = String(hour).padStart(OFFSET_PADDING_LENGTH, "0");
  const paddedMinutes = String(minutes).padStart(OFFSET_PADDING_LENGTH, "0");

  return {
    ...org,
    timezoneAbbr: zoneAbbreviation,
    utcOffset: `${sign}${paddedHour}:${paddedMinutes}`
  };
};

const findOrgById = (
  orgId: string,
  organizations?: Array<gqlType.Organization>
) => organizations && organizations.find(org => org.id === orgId);

const mergePipelineCoordinates = (
  prevPipelines: Array<gqlType.Pipeline> = [],
  nextPipelines: Array<gqlType.Pipeline> = []
): Array<gqlType.Pipeline> => {
  const byId = new Map<string, gqlType.Pipeline>();

  prevPipelines.forEach(pipeline => {
    byId.set(pipeline.id, pipeline);
  });

  nextPipelines.forEach(pipeline => {
    const prev = byId.get(pipeline.id);
    if (!prev) {
      byId.set(pipeline.id, pipeline);
      return;
    }

    byId.set(pipeline.id, {
      ...prev,
      ...pipeline,
      segments: (prev.segments || []).concat(pipeline.segments || []),
      assets: (prev.assets || []).concat(pipeline.assets || []),
      kmPosts: (prev.kmPosts || []).concat(pipeline.kmPosts || [])
    });
  });

  return Array.from(byId.values());
};

const mergeOrganizationsPage = (
  prev: GetOrganizationsDataType,
  next?: GetOrganizationsDataType
): GetOrganizationsDataType => {
  const nextOrganizations = next && next.organizations;

  if (!prev.organizations || !nextOrganizations) {
    return prev;
  }

  return {
    ...prev,
    organizations: prev.organizations.map(prevOrg => {
      const nextOrg = nextOrganizations.find(org => org.id === prevOrg.id);
      if (!nextOrg) {
        return prevOrg;
      }

      return {
        ...prevOrg,
        ...nextOrg,
        pipelines: mergePipelineCoordinates(
          prevOrg.pipelines,
          nextOrg.pipelines
        )
      };
    })
  };
};

const hasMoreCoordinatePages = (
  organizations?: Array<OrganizationWithPageInfoType>
): boolean =>
  Boolean(
    organizations &&
      organizations.some(
        org =>
          org.pipelineCoordinatesPageInfo &&
          org.pipelineCoordinatesPageInfo.hasMore
      )
  );

const getNextCoordinatesPage = (
  organizations?: Array<OrganizationWithPageInfoType>
): number => {
  if (!organizations || organizations.length === 0) {
    return INITIAL_COORDINATES_PAGE + NEXT_PAGE_INCREMENT;
  }

  const firstOrg = organizations[FIRST_ORGANIZATION_INDEX];
  const currentPage =
    firstOrg && firstOrg.pipelineCoordinatesPageInfo
      ? Number(firstOrg.pipelineCoordinatesPageInfo.page)
      : INITIAL_COORDINATES_PAGE;

  return currentPage + NEXT_PAGE_INCREMENT;
};

export const OrganizationContext = React.createContext(initialContext);
export const useOrganizationContext = () => useContext(OrganizationContext);

export const OrganizationProvider = (props: PropsType) => {
  const { error, loading, data, fetchMore } = useQuery<
    GetOrganizationsDataType
  >(GET_ORGANIZATIONS_DATA, {
    variables: {
      pipelineCoordinatesPage: INITIAL_COORDINATES_PAGE,
      pipelineCoordinatesPageSize: COORDINATES_PAGE_SIZE
    },
    notifyOnNetworkStatusChange: true
  });

  const [isFetchingCoordinatePages, setIsFetchingCoordinatePages] = useState(
    false
  );

  // Stream all remaining coordinate pages after first load.
  // Each page appends coordinate chunks to existing pipelines so the map
  // progressively fills in without blocking the initial render.
  useEffect(() => {
    if (!data || !data.organizations || isFetchingCoordinatePages) {
      return;
    }

    if (!hasMoreCoordinatePages(data.organizations)) {
      return;
    }

    let cancelled = false;

    const fetchRemainingPages = async () => {
      setIsFetchingCoordinatePages(true);

      // Track the current page locally so the loop advances correctly
      // instead of re-reading stale closure data each iteration.
      let currentPage = getNextCoordinatesPage(data.organizations);
      let nextPageHasMore = true;

      try {
        while (!cancelled && nextPageHasMore) {
          // eslint-disable-next-line no-await-in-loop
          const result = await fetchMore({
            variables: {
              pipelineCoordinatesPage: currentPage,
              pipelineCoordinatesPageSize: COORDINATES_PAGE_SIZE
            },
            updateQuery: (prevResult, { fetchMoreResult }) =>
              mergeOrganizationsPage(prevResult, fetchMoreResult)
          });

          const resultOrgs =
            result && result.data && result.data.organizations
              ? result.data.organizations
              : undefined;

          nextPageHasMore = hasMoreCoordinatePages(resultOrgs);

          if (nextPageHasMore) {
            currentPage = currentPage + NEXT_PAGE_INCREMENT;
          }
        }
      } finally {
        if (!cancelled) {
          setIsFetchingCoordinatePages(false);
        }
      }
    };

    fetchRemainingPages();

    return () => {
      cancelled = true;
    };
  }, [data, fetchMore, isFetchingCoordinatePages]);

  const [activeOrgId, setActiveOrgId] = useState<string>();
  const [organizations, setOrganizations] = useState<
    Array<OrganizationWithPageInfoType>
  >();

  if (error) {
    throw new AppError(
      "Organization information could not be retrieved",
      error.message
    );
  }

  if (!data && !loading) {
    throw new AppError(
      "Organization data could not be fetched",
      "Refresh the page to try again. If this problem persists, contact Hifi support."
    );
  }

  // The function that allows other parts of the app to change the organization
  // useCallback is used so that the components that implement this context don't render every time
  // this provider re-renders.
  const setActiveOrg = useCallback(
    (orgId: string) => {
      if (!organizations) {
        throw new Error(
          "You can't switch organizations while the application is loading"
        );
      }
      if (!findOrgById(orgId, organizations)) {
        throw new AppError(
          `Cannot switch to Organization "${orgId}" because it does not exist`,
          "Please refresh your browser and try again."
        );
      }

      setActiveOrgId(orgId);
      localStorage.setItem("hifi:activeOrg", orgId);
    },
    [organizations]
  );

  // useQuery has an internal caching mechanism that causes `data` to update more often than
  // necessary. The LiveStatus polling queries cause this provider to update as well. Doing a deep
  // comparison is a good way to check if the organizations have actually changed.
  useDeepCompareEffect(() => {
    if (data && data.organizations) {
      const normalizedOrganizations: Array<OrganizationWithPageInfoType> = data.organizations.map(
        org => ({
          ...applyDaylightSavings(org),
          pipelineCoordinatesPageInfo: org.pipelineCoordinatesPageInfo || {
            page: INITIAL_COORDINATES_PAGE,
            pageSize: COORDINATES_PAGE_SIZE,
            hasMore: false
          }
        })
      );

      setOrganizations(normalizedOrganizations);
    }
  }, [data || {}]);

  // This should only run on application startup when there is no active org ID yet.
  // If the user does not have an active org defined, grab it from localhost, or fall back to
  // picking the first one.
  useEffect(() => {
    if (organizations && !activeOrgId) {
      // User must belong to at least one org
      if (organizations.length === 0) {
        throw new AppError(
          "Your account is not associated with any organizations",
          "Contact Hifi support to resolve this issue."
        );
      }

      const previouslyActiveOrgId = localStorage.getItem("hifi:activeOrg");
      if (
        previouslyActiveOrgId &&
        findOrgById(previouslyActiveOrgId, organizations)
      ) {
        // If an org was previously active, restore it
        setActiveOrgId(previouslyActiveOrgId);
      } else {
        // Otherwise, just pick the first org
        setActiveOrgId(organizations[FIRST_ORGANIZATION_INDEX].id);
      }
    }
  }, [activeOrgId, organizations]);

  // Memoize the provider value, otherwise on every re-render the value will change triggering
  // re-renders throughout the app. If memo isn't used, the post markers on the events map will
  // flicker due to organization "changing" on every re-render.
  const providerValue = useMemo(() => {
    if (!organizations || !activeOrgId) return initialContext;

    return {
      setActiveOrg,
      organizations,
      activeOrg: findOrgById(activeOrgId, organizations)
    };
  }, [setActiveOrg, organizations, activeOrgId]);

  return (
    <OrganizationContext.Provider value={providerValue}>
      {props.children}
    </OrganizationContext.Provider>
  );
};
